// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Conduit — connectivity scoring over a graph the player built.
//
// This is the organ the prior-art panel made the condition of building the game at all, so it is
// worth being precise about what it is: the plate is a GRAPH, not a picture. Two adjacent cells are
// joined by an edge when the sides facing each other are the same live gauge; inside a cell, all
// same-gauge sides are mutually connected, except that the coupler also joins its culvert half to
// its filament half and the bypass joins neither of its two axes to the other.
//
// THREE NUMBERS COME OUT OF THAT GRAPH AND EACH IS A DIFFERENT KIND OF THING:
//   NET   union-find over the twelve vents — how much of the edge you gathered into ONE component
//   RUN   the longest SIMPLE PATH per gauge — bounded, iterative, and pinned so it is byte-identical
//   ENDS  the live sides still facing empty cells — the debt you ran up reaching for all of it
//
// EVERY LOOP IN HERE IS PINNED FOR DETERMINISM. Fixed neighbour order, row-major start nodes, an
// explicit stack over typed arrays, no Set or Map iteration anywhere on a hot path. Two peers score
// each other's plates to detect a stale build, so a difference of one point caused by iteration
// order would read to a player as an accusation.

import {
  CULVERT,
  DIRS,
  DX,
  DY,
  FILAMENT,
  live,
  opp,
  type Dir,
  type Gauge,
} from './pieces';
import {
  CELLS,
  SIZE,
  VENTS,
  VENT_COUNT,
  idx,
  inBounds,
  sidesAt,
  ventAt,
  xOf,
  yOf,
  type Plate,
} from './plate';

/**
 * A NODE is one (cell, gauge) pair — a cell can host both gauges at once (coupler, bypass), and they
 * are different nodes unless the fitting couples them. Node id is cell * 2 + (gauge - 1).
 */
export const NODES = CELLS * 2;
const nodeOf = (cell: number, g: Gauge): number => cell * 2 + (g - 1);

/** Weeps are half-edges that do not conduct. Keyed cell*4+dir so the scorer can refuse to cross. */
export type WeepSet = Uint8Array;
export const noWeeps = (): WeepSet => new Uint8Array(CELLS * 4);
export const weepKey = (x: number, y: number, d: Dir): number => idx(x, y) * 4 + d;

class DSU {
  p: Int32Array;
  constructor(n: number) {
    this.p = new Int32Array(n);
    for (let i = 0; i < n; i++) this.p[i] = i;
  }
  find(a: number): number {
    let r = a;
    while (this.p[r] !== r) r = this.p[r];
    while (this.p[a] !== r) {
      const nx = this.p[a];
      this.p[a] = r;
      a = nx;
    }
    return r;
  }
  union(a: number, b: number): void {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.p[ra < rb ? rb : ra] = ra < rb ? ra : rb;
  }
}

export interface Graph {
  dsu: DSU;
  /** Adjacency for the path search, per gauge: adj[gauge-1][node] = list of neighbour nodes. */
  adjC: Int32Array[];
  adjF: Int32Array[];
  /** Which vents are attached to the network at all (regardless of whether they are sealed). */
  ventNode: Int32Array;
}

/**
 * Build the connectivity graph.
 *
 * WHAT MAKES SEEP DIFFERENT is not this function: a mismatched seam is refused an edge by the match
 * comparison below, exactly as in every other mode. Seep's change is that such a seam is LEGAL TO
 * CREATE and carries a charge, so you may spend points to place something that does not connect —
 * the plate grows, the network does not.
 */
export function buildGraph(p: Plate, weeps: WeepSet = noWeeps()): Graph {
  const dsu = new DSU(NODES);
  const nC: number[][] = Array.from({ length: NODES }, () => []);
  const nF: number[][] = Array.from({ length: NODES }, () => []);

  // Intra-cell: the coupler joins its two gauges; everything else keeps them apart.
  for (let i = 0; i < CELLS; i++) {
    const x = xOf(i);
    const y = yOf(i);
    const s = sidesAt(p, x, y);
    if (!s) continue;
    let hasC = false;
    let hasF = false;
    for (const d of DIRS) {
      if (s[d] === CULVERT) hasC = true;
      else if (s[d] === FILAMENT) hasF = true;
    }
    if (hasC && hasF) {
      const pi = p.piece[i];
      // PIECE_ORDER index 6 is the coupler. Read from the plate so a renamed piece cannot drift.
      if (pi === 6) dsu.union(nodeOf(i, CULVERT), nodeOf(i, FILAMENT));
    }
  }

  // Inter-cell: fixed direction order, and each boundary is visited once (east and south only).
  for (let i = 0; i < CELLS; i++) {
    const x = xOf(i);
    const y = yOf(i);
    const s = sidesAt(p, x, y);
    if (!s) continue;
    for (const d of [1, 2] as Dir[]) {
      const nx = x + DX[d];
      const ny = y + DY[d];
      if (!inBounds(nx, ny)) continue;
      const t = sidesAt(p, nx, ny);
      if (!t) continue;
      const mine = s[d];
      if (!live(mine)) continue;
      // NON-CONDUCTION IS ENFORCED HERE, BY THE MATCH RULE ITSELF. A weep is by definition a seam
      // whose two sides disagree, so this line already refuses to build an edge across one — which
      // is why mutating the weep guard below changes nothing and why it is not the safety net it
      // looks like. Stated explicitly because a reader (and a test author) will otherwise assume the
      // weep set is what stops a mismatched seam carrying flow, and write a check that cannot fail.
      if (t[opp(d)] !== mine) continue;
      // Belt and braces for a future mode that records a weep on a MATCHING seam. Dead today.
      if (weeps[weepKey(x, y, d)] || weeps[weepKey(nx, ny, opp(d))]) continue;
      const a = nodeOf(i, mine);
      const b = nodeOf(idx(nx, ny), mine);
      dsu.union(a, b);
      if (mine === CULVERT) {
        nC[a].push(b);
        nC[b].push(a);
      } else {
        nF[a].push(b);
        nF[b].push(a);
      }
    }
  }

  // Vents attach to the node in the cell they touch, if that cell presents their gauge outward.
  const ventNode = new Int32Array(VENT_COUNT).fill(-1);
  for (const v of VENTS) {
    const s = sidesAt(p, v.x, v.y);
    if (!s) continue;
    if (s[v.dir] !== v.gauge) continue;
    if (weeps[weepKey(v.x, v.y, v.dir)]) continue;
    ventNode[v.id] = nodeOf(idx(v.x, v.y), v.gauge);
  }

  return {
    dsu,
    adjC: nC.map((a) => Int32Array.from(a)),
    adjF: nF.map((a) => Int32Array.from(a)),
    ventNode,
  };
}

/**
 * Which vents share a component, as a partition.
 *
 * The audit checks this SET FOR SET against its own independent per-vent BFS, not merely the counts
 * — two different partitions can trivially have the same size histogram.
 */
export function ventComponents(g: Graph): number[][] {
  const byRoot = new Map<number, number[]>();
  for (let v = 0; v < VENT_COUNT; v++) {
    if (g.ventNode[v] < 0) continue;
    const r = g.dsu.find(g.ventNode[v]);
    const list = byRoot.get(r);
    if (list) list.push(v);
    else byRoot.set(r, [v]);
  }
  // Sorted by first member so the output order is a pure function of the plate.
  return [...byRoot.values()].sort((a, b) => a[0] - b[0]);
}

/** A vent is SEALED when its component holds at least one OTHER vent. Touching one pays nothing. */
export function sealedVents(g: Graph): boolean[] {
  const out = new Array<boolean>(VENT_COUNT).fill(false);
  for (const comp of ventComponents(g)) {
    if (comp.length < 2) continue;
    for (const v of comp) out[v] = true;
  }
  return out;
}

/** Vents in the largest single component. Reported on the HUD; NOT what NET pays for — see below. */
export function largestVentComponent(g: Graph): number {
  let best = 0;
  for (const c of ventComponents(g)) if (c.length > best) best = c.length;
  return best;
}

/**
 * The sizes of every vent-holding component, largest first. NET pays over ALL of them.
 *
 * AN EARLIER DRAFT PAID ONLY FOR THE LARGEST COMPONENT AND IT WAS UNSTEERABLE. Raising that payout
 * fourfold in a sweep changed the score and did not change a single placement — largest-component
 * size and component count came back byte-identical at every multiplier. The reason is that a
 * largest-only term is a lump sum: it moves only in the rare instant two components merge, so for
 * almost every candidate placement it contributes the same constant to every option and drops out of
 * the ranking entirely. A term no search can climb is a term no player can plan toward either.
 *
 * Summing k(k-1) over every component keeps the superlinearity that makes consolidating worth more
 * than sprawling — one 4-vent network pays 12 where two pairs pay 4 — while making EVERY vent you
 * add to ANY network pay a visible marginal the moment you add it.
 */
export function ventComponentSizes(g: Graph): number[] {
  return ventComponents(g)
    .map((c) => c.length)
    .filter((n) => n >= 2)
    .sort((a, b) => b - a);
}

export interface RunResult {
  length: number;
  /** The actual path, cell-by-cell, so the audit can walk it and prove it is a real simple path. */
  path: number[];
  /** True if the search hit its expansion budget and returned a best-so-far. Must never happen. */
  bounded: boolean;
  expansions: number;
}

/**
 * Longest simple path in one gauge's subgraph.
 *
 * Longest-simple-path is NP-hard in general; this graph is tiny (<= 49 nodes per gauge, max degree
 * 4) so exhaustive DFS is fine in practice, but "in practice" is not a guarantee, so the search
 * carries an explicit expansion budget and reports whether it hit it. The sim asserts the budget is
 * never approached — a bounded result would silently make one player's score wrong and then make two
 * honest peers accuse each other of running a stale build.
 *
 * Everything about the traversal is pinned: start nodes in row-major order, neighbours in the fixed
 * order they were pushed (which is itself N,E,S,W-derived), an explicit stack, and a Uint8Array for
 * the visited set. No Map/Set iteration, no sort by anything but a number.
 */
export const RUN_BUDGET = 250_000;

export function longestRun(g: Graph, gauge: Gauge): RunResult {
  const adj = gauge === CULVERT ? g.adjC : g.adjF;
  const onPath = new Uint8Array(NODES);
  let best = 0;
  let bestPath: number[] = [];
  let expansions = 0;
  let bounded = false;

  const stack: number[] = [];

  const dfs = (start: number): void => {
    stack.length = 0;
    stack.push(start);
    onPath[start] = 1;
    // Iterative DFS carrying an explicit per-frame neighbour cursor.
    const cursor: number[] = [0];
    while (stack.length > 0) {
      if (++expansions > RUN_BUDGET) {
        bounded = true;
        break;
      }
      const node = stack[stack.length - 1];
      const ci = cursor.length - 1;
      const nbrs = adj[node];
      if (stack.length > best) {
        best = stack.length;
        bestPath = stack.slice();
      }
      if (cursor[ci] >= nbrs.length) {
        onPath[node] = 0;
        stack.pop();
        cursor.pop();
        continue;
      }
      const nxt = nbrs[cursor[ci]++];
      if (onPath[nxt]) continue;
      onPath[nxt] = 1;
      stack.push(nxt);
      cursor.push(0);
    }
    // Unwind anything the budget break left marked.
    for (const n of stack) onPath[n] = 0;
    stack.length = 0;
  };

  for (let i = 0; i < CELLS && !bounded; i++) {
    const n = nodeOf(i, gauge);
    if (adj[n].length === 0) continue;
    dfs(n);
  }

  return { length: best, path: bestPath.map((n) => Math.floor(n / 2)), bounded, expansions };
}

/**
 * Open ends: live sides still facing an EMPTY interior cell at the finish.
 *
 * Per SIDE, not per cell — a tee left hanging with two open sides costs twice, which is exactly the
 * pressure that stops "just play tees everywhere, they connect to more things".
 */
export function openEnds(p: Plate): number {
  let n = 0;
  for (let i = 0; i < CELLS; i++) {
    const x = xOf(i);
    const y = yOf(i);
    const s = sidesAt(p, x, y);
    if (!s) continue;
    for (const d of DIRS) {
      if (!live(s[d])) continue;
      const nx = x + DX[d];
      const ny = y + DY[d];
      if (!inBounds(nx, ny)) continue; // off-plate is a vent or a weep, never an open end
      if (sidesAt(p, nx, ny) === null) n++;
    }
  }
  return n;
}

/** Cells carrying a gauge that are NOT on that gauge's scored path. Flume's off-path charge. */
export function offPathCells(p: Plate, path: number[], gauge: Gauge): number {
  const on = new Uint8Array(CELLS);
  for (const c of path) on[c] = 1;
  let n = 0;
  for (let i = 0; i < CELLS; i++) {
    const s = sidesAt(p, xOf(i), yOf(i));
    if (!s) continue;
    let has = false;
    for (const d of DIRS) if (s[d] === gauge) has = true;
    if (has && !on[i]) n++;
  }
  return n;
}

/** How far a live side reaches out of the plate at a vent — used by the renderer and the audit. */
export function ventsTouched(p: Plate): boolean[] {
  const out = new Array<boolean>(VENT_COUNT).fill(false);
  for (const v of VENTS) {
    const s = sidesAt(p, v.x, v.y);
    if (s && s[v.dir] === v.gauge) out[v.id] = true;
  }
  return out;
}

export { ventAt, SIZE, idx };
